import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Search,
  ShieldAlert,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { ledgerService, type LedgerWalletDetail } from '@services/ledger.service';
import {
  newIdempotencyKey,
  treasuryService,
  type TreasuryBalance,
  type TreasuryOperation,
  type TreasuryRequest,
  type TreasuryResult,
} from '@services/treasury.service';
import { cn, formatCurrency } from '@lib/utils';
import { humanizeCode, shortId } from '@pages/finance/ledgerFormat';

// ── Operation catalogue ────────────────────────────────────────────────────
// Each operation says, in an operator's words, what it does to real money.
// `direction` is only presentational — the backend decides the actual posting.

interface OperationSpec {
  value: TreasuryOperation;
  label: string;
  /** What this does, in one sentence an ops person can act on. */
  description: string;
  /** Verb on the confirm button. */
  verb: string;
  /** True when the operation needs a destination wallet. */
  needsCounterparty?: boolean;
  /** True when the amount may be negative (hold adjustments go both ways). */
  signedAmount?: boolean;
}

const OPERATIONS: OperationSpec[] = [
  {
    value: 'refund-customer',
    label: 'Refund customer',
    description:
      'Return money to a customer, e.g. an order that was paid for but never delivered. Posts a refund against escrow and credits the customer wallet.',
    verb: 'Refund customer',
  },
  {
    value: 'refund-merchant',
    label: 'Refund merchant',
    description:
      'Return money to a merchant, e.g. commission or a delivery fee that was deducted in error.',
    verb: 'Refund merchant',
  },
  {
    value: 'credit',
    label: 'Load (credit)',
    description:
      'Put money into this wallet from Chekit funds — goodwill credit, a manual top-up, a correction in the wallet holder’s favour.',
    verb: 'Load wallet',
  },
  {
    value: 'debit',
    label: 'Deduct (debit)',
    description:
      'Take money out of this wallet back to Chekit funds — clawing back an overpayment or an incorrect credit.',
    verb: 'Deduct from wallet',
  },
  {
    value: 'transfer',
    label: 'Transfer',
    description:
      'Move money between two wallets. Both sides post in one balanced journal entry.',
    verb: 'Transfer money',
    needsCounterparty: true,
  },
  {
    value: 'adjust-hold',
    label: 'Adjust hold',
    description:
      'Change how much of this wallet is locked, without moving any balance. Releasing a hold is how you unstick money trapped against an order that already finished.',
    verb: 'Adjust hold',
    signedAmount: true,
  },
];

const inputClass =
  'w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors';

const labelClass =
  'block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5';

/** Both API clients funnel errors through `extractMessage`, so the human
 *  message (INSUFFICIENT_BALANCE and friends) is already on `.message`. */
function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

type Step = 'form' | 'preview' | 'done';

export interface TreasuryOperationModalProps {
  open: boolean;
  onClose: () => void;
  /** The wallet the operation targets. */
  walletId: string;
  /** Shown in the header so the operator can see they picked the right wallet. */
  walletLabel?: string | null;
  walletTypeCode?: string | null;
  /** Preselect an operation, e.g. `adjust-hold` from a stuck-hold row. */
  defaultOperation?: TreasuryOperation;
  defaultReferenceType?: string | null;
  defaultReferenceId?: string | null;
  defaultAmount?: number;
  /** Called after a real (non-preview) application succeeds. */
  onDone?: () => void;
}

// ── Before → after row ─────────────────────────────────────────────────────

const BalanceDelta = ({
  label,
  before,
  after,
  dangerWhenNegative,
}: {
  label: string;
  before: number | undefined;
  after: number;
  dangerWhenNegative?: boolean;
}) => {
  const changed = before !== undefined && Math.abs(after - before) > 0.001;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12px] text-[var(--text-muted)]">{label}</span>
      <span className="flex items-baseline gap-2 text-[13px] whitespace-nowrap">
        <span className={cn(changed ? 'text-[var(--text-muted)] line-through' : 'text-[var(--text-muted)]')}>
          {before === undefined ? '—' : formatCurrency(before)}
        </span>
        {changed && (
          <>
            <ArrowRight className="w-3 h-3 text-[var(--text-muted)]" strokeWidth={1.5} />
            <span
              className={cn(
                'font-semibold',
                dangerWhenNegative && after < 0
                  ? 'text-[var(--state-danger)]'
                  : 'text-[var(--text-primary)]',
              )}
            >
              {formatCurrency(after)}
            </span>
          </>
        )}
      </span>
    </div>
  );
};

export function TreasuryOperationModal({
  open,
  onClose,
  walletId,
  walletLabel,
  walletTypeCode,
  defaultOperation,
  defaultReferenceType,
  defaultReferenceId,
  defaultAmount,
  onDone,
}: TreasuryOperationModalProps) {
  const queryClient = useQueryClient();

  const [operation, setOperation] = useState<TreasuryOperation>(
    defaultOperation ?? 'refund-customer',
  );
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [referenceType, setReferenceType] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [counterpartyWalletId, setCounterpartyWalletId] = useState('');
  const [counterpartySearch, setCounterpartySearch] = useState('');
  const [counterpartyQuery, setCounterpartyQuery] = useState('');

  const [step, setStep] = useState<Step>('form');
  const [previewResult, setPreviewResult] = useState<TreasuryResult | null>(null);
  const [appliedResult, setAppliedResult] = useState<TreasuryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // One key per apply attempt. Held across the preview→apply pair so that a
  // double-click on Apply replays instead of posting the money twice.
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());

  const spec = OPERATIONS.find((o) => o.value === operation)!;

  // Reset every time the modal is opened for a (possibly different) wallet.
  useEffect(() => {
    if (!open) return;
    setOperation(defaultOperation ?? 'refund-customer');
    setAmount(defaultAmount !== undefined ? String(defaultAmount) : '');
    setReason('');
    setReferenceType(defaultReferenceType ?? '');
    setReferenceId(defaultReferenceId ?? '');
    setCounterpartyWalletId('');
    setCounterpartySearch('');
    setCounterpartyQuery('');
    setStep('form');
    setPreviewResult(null);
    setAppliedResult(null);
    setError(null);
    setIdempotencyKey(newIdempotencyKey());
  }, [open, walletId, defaultOperation, defaultReferenceType, defaultReferenceId, defaultAmount]);

  // "Before" balances come from the live wallet rows, so the preview's
  // projected figures can be shown as a real before → after change.
  const { data: targetWallet } = useQuery({
    queryKey: ['ledger', 'wallet', walletId],
    queryFn: () => ledgerService.getWallet(walletId),
    enabled: open && !!walletId,
  });

  const { data: counterpartyWallet } = useQuery({
    queryKey: ['ledger', 'wallet', counterpartyWalletId],
    queryFn: () => ledgerService.getWallet(counterpartyWalletId),
    enabled: open && !!counterpartyWalletId,
  });

  const { data: counterpartyOptions, isFetching: searchingCounterparty } = useQuery({
    queryKey: ['ledger', 'wallets', 'counterparty-search', counterpartyQuery],
    queryFn: () => ledgerService.listWallets({ q: counterpartyQuery, page: 1, size: 20 }),
    enabled: open && spec.needsCounterparty && counterpartyQuery.length > 1,
  });

  // adjust-hold direction. 'unhold' (release) is the common operator action —
  // freeing money stuck against an order that already finished.
  const [holdDirection, setHoldDirection] = useState<'hold' | 'unhold'>('unhold');

  const beforeByWallet = useMemo(() => {
    const map = new Map<string, LedgerWalletDetail>();
    if (targetWallet) map.set(targetWallet.walletId, targetWallet);
    if (counterpartyWallet) map.set(counterpartyWallet.walletId, counterpartyWallet);
    return map;
  }, [targetWallet, counterpartyWallet]);

  // The backend validates `amount` as strictly positive on EVERY operation
  // (@DecimalMin 0.01) and reads hold direction from a separate `direction`
  // field — so a signed amount is never sent.
  const parsedAmount = Math.abs(Number(amount));
  const amountValid =
    amount.trim() !== '' && Number.isFinite(parsedAmount) && parsedAmount > 0;
  const reasonValid = reason.trim().length > 0;
  const counterpartyValid = !spec.needsCounterparty || !!counterpartyWalletId;
  const canPreview = amountValid && reasonValid && counterpartyValid;

  const buildPayload = (): TreasuryRequest => ({
    walletId,
    amount: parsedAmount,
    reason: reason.trim(),
    counterpartyWalletId: spec.needsCounterparty ? counterpartyWalletId : undefined,
    // Only adjust-hold carries a direction; the backend defaults it to 'unhold'.
    direction: operation === 'adjust-hold' ? holdDirection : undefined,
    referenceType: referenceType.trim() || undefined,
    referenceId: referenceId.trim() || undefined,
    idempotencyKey,
  });

  const previewMutation = useMutation({
    mutationFn: () => treasuryService.preview(operation, buildPayload()),
    onSuccess: (result) => {
      setPreviewResult(result);
      setError(null);
      setStep('preview');
    },
    onError: (err) => setError(apiMessage(err, 'Could not preview this operation.')),
  });

  const applyMutation = useMutation({
    mutationFn: () => treasuryService.apply(operation, buildPayload()),
    onSuccess: (result) => {
      setAppliedResult(result);
      setError(null);
      setStep('done');
      toast.success(
        result.idempotentReplay
          ? 'Already applied — nothing moved a second time.'
          : `${spec.label} applied.`,
      );
      // Anything showing a balance is now stale.
      queryClient.invalidateQueries({ queryKey: ['ledger'] });
      queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onDone?.();
    },
    onError: (err) => setError(apiMessage(err, 'Could not apply this operation.')),
  });

  const busy = previewMutation.isPending || applyMutation.isPending;

  const backToForm = () => {
    setStep('form');
    setPreviewResult(null);
    setError(null);
    // A changed request is a new attempt and must not reuse the old key.
    setIdempotencyKey(newIdempotencyKey());
  };

  const startAnother = () => {
    setAmount('');
    setReason('');
    setReferenceType('');
    setReferenceId('');
    setPreviewResult(null);
    setAppliedResult(null);
    setError(null);
    setStep('form');
    setIdempotencyKey(newIdempotencyKey());
  };

  if (!open) return null;

  const projected: TreasuryBalance[] =
    (step === 'done' ? appliedResult?.balances : previewResult?.balances) ?? [];

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 p-4">
      <div className="my-8 w-full max-w-2xl rounded-3xl bg-[var(--bg-surface)] shadow-lift">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-8 py-6">
          <div className="min-w-0">
            <h2 className="text-[18px] font-semibold text-[var(--text-primary)]">
              Manage money
            </h2>
            <p className="mt-1 truncate text-[13px] text-[var(--text-muted)]">
              {walletLabel ?? 'Wallet'}
              {walletTypeCode ? ` · ${humanizeCode(walletTypeCode)}` : ''} ·{' '}
              {shortId(walletId)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-8 py-6">
          {/* ── Step 1: what do you want to do ─────────────────────────── */}
          {step === 'form' && (
            <div className="space-y-6">
              <div>
                <span className={labelClass}>Operation</span>
                <div className="flex flex-wrap gap-2">
                  {OPERATIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => {
                        setOperation(o.value);
                        setError(null);
                      }}
                      className={cn(
                        'rounded-full border px-4 py-2 text-[13px] font-medium transition-colors',
                        operation === o.value
                          ? 'border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-primary)]'
                          : 'border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]',
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-[13px] text-[var(--text-secondary)]">
                  {spec.description}
                </p>
              </div>

              {/* Current position, so the operator sizes the amount correctly */}
              {targetWallet && (
                <div className="grid grid-cols-3 gap-4 rounded-2xl bg-[var(--bg-base)] px-5 py-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Balance
                    </div>
                    <div className="text-[15px] font-medium text-[var(--text-primary)]">
                      {formatCurrency(targetWallet.balance, targetWallet.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Held
                    </div>
                    <div className="text-[15px] font-medium text-[var(--state-warning)]">
                      {formatCurrency(targetWallet.heldBalance, targetWallet.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Available
                    </div>
                    <div
                      className={cn(
                        'text-[15px] font-medium',
                        targetWallet.availableBalance < 0
                          ? 'text-[var(--state-danger)]'
                          : 'text-[var(--text-primary)]',
                      )}
                    >
                      {formatCurrency(targetWallet.availableBalance, targetWallet.currency)}
                    </div>
                  </div>
                </div>
              )}

              {/* Destination wallet (transfer only) */}
              {spec.needsCounterparty && (
                <div>
                  <label className={labelClass} htmlFor="treasury-counterparty">
                    Destination wallet *
                  </label>
                  {counterpartyWalletId ? (
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-[var(--border-default)] px-4 py-3">
                      <div className="min-w-0">
                        <div className="truncate text-[14px] font-medium text-[var(--text-primary)]">
                          {counterpartyWallet?.ownerLabel ??
                            humanizeCode(counterpartyWallet?.typeCode ?? '')}
                        </div>
                        <div className="text-[12px] text-[var(--text-muted)]">
                          {counterpartyWallet?.typeCode ?? shortId(counterpartyWalletId)}
                          {counterpartyWallet &&
                            ` · available ${formatCurrency(counterpartyWallet.availableBalance)}`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCounterpartyWalletId('')}
                        className="shrink-0 text-[13px] font-medium text-[var(--text-accent)] hover:underline"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          setCounterpartyQuery(counterpartySearch.trim());
                        }}
                        className="relative"
                      >
                        <Search
                          className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]"
                          strokeWidth={1.5}
                        />
                        <input
                          id="treasury-counterparty"
                          type="text"
                          value={counterpartySearch}
                          onChange={(e) => setCounterpartySearch(e.target.value)}
                          placeholder="Search by owner name, then press Enter"
                          className={cn(inputClass, 'pl-10')}
                        />
                      </form>
                      {searchingCounterparty && (
                        <p className="mt-2 text-[12px] text-[var(--text-muted)]">Searching...</p>
                      )}
                      {counterpartyOptions && counterpartyOptions.data.length > 0 && (
                        <div className="mt-2 max-h-56 overflow-y-auto rounded-2xl border border-[var(--border-subtle)] divide-y divide-[var(--border-subtle)]">
                          {counterpartyOptions.data
                            .filter((w) => w.walletId !== walletId)
                            .map((w) => (
                              <button
                                key={w.walletId}
                                type="button"
                                onClick={() => setCounterpartyWalletId(w.walletId)}
                                className="block w-full px-4 py-3 text-left transition-colors hover:bg-[var(--bg-base)]"
                              >
                                <div className="truncate text-[14px] text-[var(--text-primary)]">
                                  {w.ownerLabel ?? humanizeCode(w.typeCode)}
                                </div>
                                <div className="text-[12px] text-[var(--text-muted)]">
                                  {w.typeCode} · available {formatCurrency(w.availableBalance)}
                                </div>
                              </button>
                            ))}
                        </div>
                      )}
                      {counterpartyOptions && counterpartyOptions.data.length === 0 && (
                        <p className="mt-2 text-[12px] text-[var(--text-muted)]">
                          No wallets matched that search.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Amount */}
              <div>
                <label className={labelClass} htmlFor="treasury-amount">
                  Amount (KES) *
                </label>
                <input
                  id="treasury-amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                />
                {spec.signedAmount ? (
                  <div className="mt-3">
                    <div className="flex gap-2">
                      {(['unhold', 'hold'] as const).map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setHoldDirection(d)}
                          className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                            holdDirection === d
                              ? 'border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-primary)]'
                              : 'border-[var(--border-subtle)] text-[var(--text-muted)]'
                          }`}
                        >
                          {d === 'unhold' ? 'Release hold' : 'Place hold'}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-[12px] text-[var(--text-muted)]">
                      {holdDirection === 'unhold'
                        ? 'Releasing frees locked money back into available balance. No balance moves — it only becomes spendable again.'
                        : 'Placing locks part of the available balance so it cannot be spent. No balance moves.'}
                    </p>
                  </div>
                ) : (
                  amount.trim() !== '' &&
                  !amountValid && (
                    <p className="mt-2 text-[12px] text-[var(--state-danger)]">
                      Enter an amount greater than zero.
                    </p>
                  )
                )}
              </div>

              {/* Reason — mandatory */}
              <div>
                <label className={labelClass} htmlFor="treasury-reason">
                  Reason *
                </label>
                <textarea
                  id="treasury-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Why is this money moving? Recorded on the journal entry."
                  className={cn(inputClass, 'resize-none')}
                />
                {!reasonValid && (
                  <p className="mt-2 text-[12px] text-[var(--text-muted)]">
                    Required — an unexplained money movement cannot be audited later.
                  </p>
                )}
              </div>

              {/* Optional linkage */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="treasury-ref-type">
                    Reference type (optional)
                  </label>
                  <input
                    id="treasury-ref-type"
                    type="text"
                    value={referenceType}
                    onChange={(e) => setReferenceType(e.target.value)}
                    placeholder="order, basket, dispute..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="treasury-ref-id">
                    Reference id (optional)
                  </label>
                  <input
                    id="treasury-ref-id"
                    type="text"
                    value={referenceId}
                    onChange={(e) => setReferenceId(e.target.value)}
                    placeholder="UUID"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: exactly what will change ───────────────────────── */}
          {step === 'preview' && previewResult && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 rounded-2xl border border-[var(--state-warning-border)] bg-[var(--state-warning-bg)] px-5 py-4">
                <ShieldAlert
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                    Nothing has moved yet
                  </p>
                  <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
                    This is a dry run. Check the balances below, then confirm to post
                    the journal entry for real.
                  </p>
                </div>
              </div>

              <div className="space-y-2 rounded-2xl bg-[var(--bg-base)] px-5 py-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[12px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Operation
                  </span>
                  <span className="text-[14px] font-medium text-[var(--text-primary)]">
                    {spec.label}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[12px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Amount
                  </span>
                  <span className="font-display text-[24px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                    {formatCurrency(previewResult.amount)}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span className="shrink-0 text-[12px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Reason
                  </span>
                  <span className="text-right text-[13px] text-[var(--text-secondary)]">
                    {previewResult.reason}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">
                  Projected balances
                </h3>
                {projected.length === 0 && (
                  <p className="text-[13px] text-[var(--text-muted)]">
                    The preview returned no balance changes.
                  </p>
                )}
                {projected.map((b) => {
                  const before = beforeByWallet.get(b.walletId);
                  return (
                    <div
                      key={b.walletId}
                      className="space-y-2 rounded-2xl border border-[var(--border-subtle)] px-5 py-4"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[14px] font-medium text-[var(--text-primary)]">
                          {before?.ownerLabel ?? humanizeCode(b.walletType)}
                        </span>
                        <span className="text-[12px] text-[var(--text-muted)]">
                          {b.walletType} · {shortId(b.walletId)}
                        </span>
                      </div>
                      <BalanceDelta label="Balance" before={before?.balance} after={b.balance} />
                      <BalanceDelta
                        label="Held"
                        before={before?.heldBalance}
                        after={b.heldBalance}
                      />
                      <BalanceDelta
                        label="Available"
                        before={before?.availableBalance}
                        after={b.availableBalance}
                        dangerWhenNegative
                      />
                      {b.availableBalance < 0 && (
                        <p className="flex items-center gap-2 text-[12px] text-[var(--state-danger)]">
                          <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
                          This leaves the wallet with a negative available balance.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 3: what actually happened ─────────────────────────── */}
          {step === 'done' && appliedResult && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 rounded-2xl border border-[var(--state-success-border)] bg-[var(--state-success-bg)] px-5 py-4">
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-success)]"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                    {appliedResult.idempotentReplay
                      ? 'Already applied earlier'
                      : `${spec.label} applied`}
                  </p>
                  <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
                    {appliedResult.idempotentReplay
                      ? 'This exact request had already been posted, so no money moved a second time.'
                      : `${formatCurrency(appliedResult.amount)} posted as a balanced journal entry.`}
                  </p>
                </div>
              </div>

              {appliedResult.journalEntryId && (
                <div className="rounded-2xl bg-[var(--bg-base)] px-5 py-4">
                  <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Journal entry
                  </div>
                  <div className="mt-1 break-all font-mono text-[13px] text-[var(--text-primary)]">
                    {appliedResult.journalEntryId}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">
                  New balances
                </h3>
                {projected.map((b) => (
                  <div
                    key={b.walletId}
                    className="space-y-2 rounded-2xl border border-[var(--border-subtle)] px-5 py-4"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-[14px] font-medium text-[var(--text-primary)]">
                        {humanizeCode(b.walletType)}
                      </span>
                      <span className="text-[12px] text-[var(--text-muted)]">
                        {shortId(b.walletId)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Balance
                        </div>
                        <div className="text-[14px] font-medium text-[var(--text-primary)]">
                          {formatCurrency(b.balance)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Held
                        </div>
                        <div className="text-[14px] font-medium text-[var(--state-warning)]">
                          {formatCurrency(b.heldBalance)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Available
                        </div>
                        <div
                          className={cn(
                            'text-[14px] font-medium',
                            b.availableBalance < 0
                              ? 'text-[var(--state-danger)]'
                              : 'text-[var(--text-primary)]',
                          )}
                        >
                          {formatCurrency(b.availableBalance)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Errors from the API — INSUFFICIENT_BALANCE and friends */}
          {error && (
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-4">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]"
                strokeWidth={1.5}
              />
              <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-3 border-t border-[var(--border-subtle)] px-8 py-5">
          <p className="text-[12px] text-[var(--text-muted)]">
            {step === 'form' && 'Step 1 of 2 — preview first, then confirm.'}
            {step === 'preview' && 'Step 2 of 2 — confirm to move real money.'}
            {step === 'done' && 'Done.'}
          </p>

          <div className="flex items-center gap-3">
            {step === 'form' && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-[var(--border-default)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => previewMutation.mutate()}
                  disabled={!canPreview || busy}
                  className="rounded-full bg-[var(--text-primary)] px-5 py-2.5 text-[14px] font-medium text-[var(--bg-surface)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {previewMutation.isPending ? 'Previewing...' : 'Preview'}
                </button>
              </>
            )}

            {step === 'preview' && (
              <>
                <button
                  type="button"
                  onClick={backToForm}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:opacity-40"
                >
                  <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => applyMutation.mutate()}
                  disabled={busy}
                  className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[14px] font-semibold text-[var(--state-danger)] transition-colors hover:border-[var(--state-danger)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {applyMutation.isPending ? 'Applying...' : `Confirm — ${spec.verb}`}
                </button>
              </>
            )}

            {step === 'done' && (
              <>
                <button
                  type="button"
                  onClick={startAnother}
                  className="rounded-full border border-[var(--border-default)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
                >
                  Another operation
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full bg-[var(--text-primary)] px-5 py-2.5 text-[14px] font-medium text-[var(--bg-surface)] transition-opacity hover:opacity-90"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
